// İSG uzmanı portalı — Firma bilgileri (api-contract §10.1, tenant-scoped). Çağıranın
// KENDİ firması: tek form, liste yok, firma `uuid` seçtirilmez/gönderilmez (token'dan).
// `GET /company` → doldur → düzenle → `PUT /company`. RHF + Zod; kaydedince bildirim.
//
// §9 admin firma yönetiminden (CompaniesPage — cross-tenant liste/oluştur) AYRIDIR.

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Field,
  Input,
  Spinner,
  resolveApiErrorMessage,
  settingsApi,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { settingsKeys } from '../../lib/settingsKeys';

/** Boş dizeyi `null`'a indirger (opsiyonel alanlar backend'e `null` gider). */
function nullable(value: string | undefined): string | null {
  return value && value.trim().length > 0 ? value.trim() : null;
}

export function SettingsCompanyPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<string | null>(null);

  // Gate ile PAYLAŞILAN sorgu (tek istek) — firma verisini forma besler.
  const query = useQuery({
    queryKey: settingsKeys.company(),
    queryFn: () => settingsApi.getCompany(),
  });

  const schema = useMemo(
    () =>
      z.object({
        name: z.string().trim().min(1, t('settings.validation.nameRequired')),
        legalName: z.string().trim().optional(),
        taxNumber: z.string().trim().optional(),
        taxOffice: z.string().trim().optional(),
        address: z.string().trim().optional(),
        phone: z.string().trim().optional(),
        email: z.string().trim().optional(),
      }),
    [t],
  );
  type FormValues = z.infer<typeof schema>;

  const company = query.data;
  const { register, handleSubmit, formState } = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: {
      name: company?.name ?? '',
      legalName: company?.legalName ?? '',
      taxNumber: company?.taxNumber ?? '',
      taxOffice: company?.taxOffice ?? '',
      address: company?.address ?? '',
      phone: company?.phone ?? '',
      email: company?.email ?? '',
    },
  });

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      settingsApi.updateCompany({
        name: values.name.trim(),
        legalName: nullable(values.legalName),
        taxNumber: nullable(values.taxNumber),
        taxOffice: nullable(values.taxOffice),
        address: nullable(values.address),
        phone: nullable(values.phone),
        email: nullable(values.email),
      }),
    onSuccess: (updated) => {
      // Yeni firma verisini cache'e yaz (gate + form aynı anahtarı okur).
      queryClient.setQueryData(settingsKeys.company(), updated);
      setMessage(t('settings.company.updated'));
    },
  });

  return (
    <div className="flex flex-col gap-5">
      {message ? <Alert tone="success">{message}</Alert> : null}

      <Card>
        <CardContent>
          {query.isPending ? (
            <div className="flex min-h-[30vh] items-center justify-center">
              <Spinner label={t('settings.common.loading')} />
            </div>
          ) : query.isError ? (
            <Alert tone="danger">{resolveApiErrorMessage(query.error, t)}</Alert>
          ) : (
            <form
              className="flex max-w-2xl flex-col gap-4"
              onSubmit={handleSubmit((values) => {
                setMessage(null);
                mutation.mutate(values);
              })}
              noValidate
            >
              {mutation.isError ? (
                <Alert tone="danger">{resolveApiErrorMessage(mutation.error, t)}</Alert>
              ) : null}

              <Field
                htmlFor="company-name"
                label={t('settings.company.field.name')}
                error={formState.errors.name?.message}
                required
              >
                <Input
                  id="company-name"
                  invalid={Boolean(formState.errors.name)}
                  {...register('name')}
                />
              </Field>

              <Field htmlFor="company-legal" label={t('settings.company.field.legalName')}>
                <Input id="company-legal" {...register('legalName')} />
              </Field>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field htmlFor="company-tax" label={t('settings.company.field.taxNumber')}>
                  <Input id="company-tax" {...register('taxNumber')} />
                </Field>
                <Field htmlFor="company-taxoffice" label={t('settings.company.field.taxOffice')}>
                  <Input id="company-taxoffice" {...register('taxOffice')} />
                </Field>
              </div>

              <Field htmlFor="company-address" label={t('settings.company.field.address')}>
                <Input id="company-address" {...register('address')} />
              </Field>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field htmlFor="company-phone" label={t('settings.company.field.phone')}>
                  <Input id="company-phone" type="tel" {...register('phone')} />
                </Field>
                <Field htmlFor="company-email" label={t('settings.company.field.email')}>
                  <Input id="company-email" type="email" {...register('email')} />
                </Field>
              </div>

              <div className="flex items-center justify-end pt-1">
                <Button type="submit" loading={mutation.isPending}>
                  {mutation.isPending ? (
                    <Spinner label={t('settings.common.save')} />
                  ) : (
                    t('settings.common.save')
                  )}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
